import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ShieldCheck, Clock, LogOut, Mail } from "lucide-react";
import Logo from "@/components/Logo";

export default function PendingApproval() {
  const { user, logout, setUser } = useAuth();

  // Poll for approval every 20s
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.subscription_status?.is_active) {
          localStorage.setItem("taxinpho_user", JSON.stringify(data));
          setUser(data);
          window.location.href = "/";
        }
      } catch (e) { /* ignore */ }
    }, 20000);
    return () => clearInterval(id);
  }, [setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] px-6 py-12">
      <div className="w-full max-w-lg bg-white border border-zinc-200 p-8" data-testid="pending-approval-page">
        <div className="mb-6">
          <Logo className="h-12 w-auto" />
        </div>

        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-[#039855]" />
          <p className="label-caps text-[#039855]">Payment received</p>
        </div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
          Awaiting admin verification.
        </h1>
        <p className="text-zinc-600 leading-relaxed mb-6">
          Hi <strong>{user?.name}</strong>, thank you for subscribing to TAXINPHO. We&apos;ve received your payment.
          The TAXINPHO admin team will verify your account shortly and grant you full access.
        </p>

        <div className="bg-[#EFF4FF] border border-[#002FA7] p-4 mb-6">
          <div className="flex items-start gap-2">
            <Clock size={16} className="text-[#002FA7] mt-0.5" />
            <div>
              <p className="font-semibold text-sm">What happens next?</p>
              <p className="text-xs text-zinc-700 mt-1 leading-relaxed">
                Our team will review your account in the next few hours.
                Once approved, you&apos;ll have <strong>full admin access</strong> — clients, invoices,
                returns, tasks, AI assistant, everything. This page will auto-refresh when access is granted.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-200 pt-4 mb-6">
          <p className="label-caps text-[10px] mb-2">Account on file</p>
          <div className="text-sm space-y-1">
            <div><span className="text-zinc-500">Name:</span> {user?.name}</div>
            <div><span className="text-zinc-500">Email:</span> {user?.email}</div>
            {user?.subscription_status?.current_period_end && (
              <div>
                <span className="text-zinc-500">Plan ends:</span>{" "}
                {new Date(user.subscription_status.current_period_end).toLocaleDateString("en-IN", {
                  day: "2-digit", month: "long", year: "numeric",
                })}
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-zinc-500 mb-6 flex items-center gap-1">
          <Mail size={12} />
          Need help? Email support@taxinpho.com with your payment reference.
        </p>

        <button
          onClick={logout}
          data-testid="pending-logout"
          className="w-full border border-zinc-300 px-4 py-2.5 text-sm font-semibold hover:bg-zinc-50 flex items-center justify-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}
